import {FC, useEffect, useState} from 'react';
import {Box, Button} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';

import {getErrorMessageFromApiError} from '@shared/api';
import {
  ApiSportsMappingValue,
  MappingKind,
  SportApisportsRefDTO,
} from '../../api/types';
import {useUpdateMapping} from '../../apiHooks';
import {ApiSportsMappingSelect} from '../ApiSportsMappingSelect';

type Props = {
  kind: MappingKind;
  categorySlug?: string;
  entityId?: string;
  // The mapping returned by the provider-metadata request (id + name only).
  initial: SportApisportsRefDTO | null;
  label?: string;
  // Required by the matches search; ignored for the other kinds.
  season?: string;
};

export const ApiSportsMappingField: FC<Props> = ({
  kind,
  categorySlug,
  entityId,
  initial,
  label,
  season,
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: updateMapping, isPending: isSaving} = useUpdateMapping();
  const [value, setValue] = useState<ApiSportsMappingValue | null>(null);

  // The saved baseline is the metadata mapping ref (empty until something is
  // actually saved). Save is shown whenever the selected value differs from it.
  const savedId = initial?.id ?? null;

  // Prefill the dropdown from the saved metadata mapping ref (id + name only);
  // the select enriches it with the full record once the mappings request
  // resolves.
  const prefill: ApiSportsMappingValue | null = initial
    ? {id: initial.id, name: initial.name, logo: '', country: ''}
    : null;

  // The select enriches the prefill with the full record (country/logo) from
  // the mappings search.
  useEffect(() => {
    setValue(prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.id, prefill?.name]);

  const hasChanged = !!value && value.id !== savedId;

  const handleSave = async () => {
    if (!value || !categorySlug || !entityId) return;

    try {
      await updateMapping({categorySlug, kind, entityId, id: value.id});
      enqueueSnackbar('Mapping updated successfully', {variant: 'success'});
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';
      enqueueSnackbar(`Could not update mapping. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={1} mt={3.5}>
      <Box flex={1}>
        <ApiSportsMappingSelect
          kind={kind}
          categorySlug={categorySlug}
          entityId={entityId}
          value={value}
          onChange={setValue}
          label={label}
          season={season}
        />
      </Box>
      {hasChanged && (
        <Button onClick={handleSave} disabled={isSaving}>
          Save
        </Button>
      )}
    </Box>
  );
};
